import React, { useState, useRef, useCallback } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  Node,
  ReactFlowInstance,
  NodeTypes,
  ConnectionMode,
  ConnectionLineType,
  ReactFlowProvider,
} from "@xyflow/react";
import { default as InputNode } from "@/components/nodes/Input_Node";
import { default as OpenAINode } from "@/components/nodes/OpenAI_Node";
import { default as OutputNode } from "@/components/nodes/Output_Node";
import { default as TextNode } from "@/components/nodes/Text_Node";
import { useFlow } from "@/lib/store";

import "@xyflow/react/dist/base.css";
import { ButtonEdge } from "@/components/ButtonEdge";
import AnthropicNode from "@/components/nodes/Anthropic_Node";
import LLamaNode from "@/components/nodes/LLama_Node";
import ChatMemoryNode from "@/components/nodes/ChatMemory_Node";
import TimeNode from "@/components/nodes/Time_Node";
import UrlLoaderNode from "@/components/nodes/URL_Node";

const gridSize = 20;
const proOptions = { hideAttribution: true };
const nodeTypes: NodeTypes = {
  customInput: InputNode,
  openai: OpenAINode,
  anthropic: AnthropicNode,
  llama: LLamaNode,
  customOutput: OutputNode,
  text: TextNode,
  chat_memory: ChatMemoryNode,
  time: TimeNode,
  url_loader: UrlLoaderNode,
};

const edgeTypes = {
  buttonEdge: ButtonEdge,
};

const FlowContent: React.FC = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  const {
    nodes,
    edges,
    getNodeID,
    addNode,
    onNodesChange,
    onEdgesChange,
    onConnect,
  } = useFlow();

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (reactFlowBounds && reactFlowInstance) {
        const appData = event.dataTransfer.getData("application/reactflow");
        if (appData) {
          const { nodeType } = JSON.parse(appData);

          if (typeof nodeType === "undefined" || !nodeType) {
            return;
          }

          const position = reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          });

          const nodeID = getNodeID(nodeType);
          const newNode: Node = {
            id: nodeID,
            type: nodeType,
            position,
            dragHandle: ".dragHandle",
            data: {
              id: nodeID,
              nodeType: nodeType,
            },
          };

          addNode(newNode);
        }
      }
    },
    [reactFlowInstance, getNodeID, addNode],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  return (
    <div ref={reactFlowWrapper} className="w-full h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onInit={setReactFlowInstance}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={proOptions}
        snapGrid={[gridSize, gridSize]}
        connectionMode={ConnectionMode.Loose}
        connectionLineType={ConnectionLineType.SmoothStep}
      >
        <Background color="#4e50d675" gap={gridSize} />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
};

export const PipelineUI: React.FC = () => {
  return (
    <ReactFlowProvider>
      <FlowContent />
    </ReactFlowProvider>
  );
};
